import { FEATURES } from '@shared/config/features';
import type { CharacterCustomization } from '@shared/game/types';

export interface MinigameStateSnapshot {
  readonly phase: string;
  readonly roundIndex?: number;
  readonly serverTick?: number;
  readonly phaseId?: string;
  readonly flow?: MinigameFlowSnapshot;
  readonly lastProcessedInputSeq?: Readonly<Record<string, number>>;
  readonly payload: unknown;
}

export type MinigameFlowPhase =
  | 'instructions'
  | 'round_countdown'
  | 'round_play'
  | 'round_standings'
  | 'finishing';

export interface MinigameFlowSnapshot {
  readonly protocolVersion: 1;
  readonly phase: MinigameFlowPhase;
  readonly phaseId: string;
  readonly roundIndex?: number;
  readonly roundCount: number;
  readonly inputOpen: boolean;
  readonly inputPhaseId?: string;
  readonly phaseStartedAtMs?: number;
  readonly phaseEndsAtMs?: number;
  readonly inputOpensAtMs?: number;
  readonly inputClosesAtMs?: number;
}

export interface MinigameSnapshot extends MinigameStateSnapshot {
  readonly instanceId: string;
  readonly minigameId: string;
  readonly serverNowMs: number;
  readonly sequence: number;
}

export interface MinigameReadyState {
  readonly instanceId: string;
  readonly minigameId: string;
  readonly phaseId?: string;
  readonly readyPlayerIds: readonly string[];
  readonly requiredPlayerIds: readonly string[];
  readonly serverNowMs: number;
  readonly playStartsAtMs?: number;
}

export interface MinigameLiveEvent<TEvent = unknown> {
  readonly instanceId: string;
  readonly minigameId: string;
  readonly serverNowMs: number;
  readonly sequence: number;
  readonly roundIndex?: number;
  readonly phaseId?: string;
  readonly event: TEvent;
}

export interface MinigameServerLiveEvent<TEvent = unknown> {
  readonly roundIndex?: number;
  readonly phaseId?: string;
  readonly event: TEvent;
}

export interface MinigameEarlyStart {
  readonly playStartsAtMs: number;
}

export interface MinigameInputMetadata {
  readonly inputPhaseId?: string;
  readonly inputSeq?: number;
  readonly clientTick?: number;
  readonly clientCommandMs?: number;
  readonly estimatedServerNowMs?: number;
  readonly clockOffsetMs?: number;
  readonly rttEstimateMs?: number;
  readonly renderDelayMs?: number;
  readonly receivedAtMs?: number;
  readonly estimatedCommandServerMs?: number;
}

export interface MinigameInputReceipt {
  readonly inputSeq?: number;
  readonly clientTick?: number;
  readonly sentAtMs?: number;
  readonly durationMs?: number;
}

export interface MinigameRewardGrant {
  readonly playerId: string;
  readonly itemIds: string[];
}

export interface Minigame {
  readonly id: string;
  readonly displayName: string;
  readonly minPlayers: number;
  readonly maxPlayers: number;
  /**
   * Whether this minigame runs as a realtime simulation (server ticks, snapshot
   * broadcasts, predicted input) or a turn-based one-shot. Drives client-side
   * music selection on minigame_start (realtime → music-minigame-realtime,
   * turn-based → music-minigame-solo).
   */
  readonly kind: 'realtime' | 'turn-based';
  startServer(args: MinigameStartArgs): MinigameServerHandle;
  startClient(args: MinigameStartArgs): MinigameClientHandle;
}

export interface MinigameStartArgs {
  readonly instanceId?: string;
  readonly roomId?: string;
  readonly players: ReadonlyArray<{
    readonly id: string;
    readonly displayName: string;
    readonly customization?: CharacterCustomization;
  }>;
  readonly seed: number;
  readonly checkpoint?: unknown;
  readonly characterAdapter?: {
    buildRig(customization: CharacterCustomization): unknown;
  };
  readonly onReady?: () => void;
  readonly onRoundResults?: (results: MinigamePerRoundResults) => void;
  readonly onState?: (snapshot: MinigameStateSnapshot) => void;
  readonly onEvent?: (event: MinigameServerLiveEvent) => void;
}

export interface MinigameClientStartArgs<TInput = unknown> extends MinigameStartArgs {
  readonly localPlayerId?: string;
  readonly onSubmit?: (input: TInput, roundIndex: number) => MinigameInputReceipt | void;
}

export interface MinigameServerHandle {
  readonly results: Promise<MinigameRoundResults>;
  /**
   * Optional one-shot input ingestion — clients call this (via dev harness or
   * future transport) to submit player input (e.g. Animal Count's numeric guess).
   * Backwards-compatible: existing minigames without input needs (e.g. _stub) can
   * omit this field. Per Decision 14 of the codex-pipeline spec, added during
   * Phase 1 (Animal Count) when the v1 light interface proved insufficient for
   * one-shot multiplayer input.
   */
  send?(playerId: string, message: unknown, metadata?: MinigameInputMetadata): void;
  getSnapshot?(): MinigameStateSnapshot | null;
  getCheckpoint?(): unknown;
  requestEarlyStart?(nowMs: number): MinigameEarlyStart | null;
  dispose(): void;
}

export interface RealtimeMinigameServerHandle extends MinigameServerHandle {
  readonly mode: 'realtime';
  readonly simulationTickMs?: number;
  readonly snapshotIntervalMs?: number;
  tick(deltaMs: number): void;
  getSnapshot(): MinigameStateSnapshot | null;
}

export interface MinigameClientHandle {
  applyServerState?(snapshot: MinigameSnapshot): void;
  applyLiveEvent?(event: MinigameLiveEvent): void;
  applyReadyState?(state: MinigameReadyState): void;
  dispose(): void;
}

export interface MinigameRoundResults {
  readonly placements: ReadonlyArray<{
    readonly playerId: string;
    readonly rank: number;
    readonly score: number;
  }>;
}

export interface MinigamePerRoundResults {
  readonly roundIndex: number;
  readonly phaseId?: string;
  readonly placements: ReadonlyArray<{
    readonly playerId: string;
    readonly rank: number;
    readonly count: number;
    readonly score: number;
  }>;
}

export class DuplicateMinigameIdError extends Error {
  constructor(id: string) {
    super(`Minigame with id "${id}" is already registered`);
    this.name = 'DuplicateMinigameIdError';
  }
}

export class MinigameRegistry {
  private readonly entries = new Map<string, Minigame>();

  register(minigame: Minigame): void {
    if (this.entries.has(minigame.id)) {
      throw new DuplicateMinigameIdError(minigame.id);
    }
    this.entries.set(minigame.id, minigame);
  }

  get(id: string): Minigame | undefined {
    return this.entries.get(id);
  }

  list(): Minigame[] {
    return Array.from(this.entries.values());
  }

  available(): Minigame[] {
    if (!FEATURES.MINIGAMES_ENABLED) return [];
    const allowed = new Set(FEATURES.MINIGAMES_AVAILABLE);
    return this.list().filter((m) => allowed.has(m.id));
  }

  pickRandom(rng: () => number): Minigame | null {
    const candidates = this.available();
    if (candidates.length === 0) return null;
    const r = rng();
    const clamped = Math.min(Math.max(r, 0), 0.9999999999);
    const idx = Math.floor(clamped * candidates.length);
    return candidates[idx] ?? null;
  }
}

export const minigameRegistry = new MinigameRegistry();
